import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users, UserPlus, Search, MoreHorizontal, Edit, Pause, Play, 
  Copy, Eye, TrendingUp, DollarSign, UserCheck, Link2, Loader2,
  RefreshCw, ExternalLink, Mail, Phone, Building2
} from 'lucide-react';
import { affiliateAPI } from '../api';

const statusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-slate-100 text-slate-800',
  suspended: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800'
};

export const AffiliateManagementPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalReferrals: 0,
    totalEarnings: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    contact_name: '',
    email: '',
    phone: '',
    company_name: '',
    commission_rate: 0.10,
    notes: ''
  });

  const fetchAffiliates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      
      const res = await affiliateAPI.getAll(params);
      const data = res.data || [];
      setAffiliates(data);
      
      // Calculate stats
      const activeCount = data.filter(a => a.status === 'active').length;
      const totalReferrals = data.reduce((sum, a) => sum + (a.total_referrals || 0), 0);
      const totalEarnings = data.reduce((sum, a) => sum + (a.total_earnings || 0), 0);
      
      setStats({
        total: data.length,
        active: activeCount,
        totalReferrals,
        totalEarnings
      });
    } catch (error) {
      console.error('Failed to fetch affiliates:', error);
      toast.error('Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  const handleCreate = async () => {
    if (!formData.contact_name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      setSaving(true);
      const res = await affiliateAPI.create(formData);
      toast.success('Affiliate created successfully');
      setTempPassword(res.data.temporary_password);
      setShowCreateDialog(false);
      fetchAffiliates();
      resetForm();
    } catch (error) {
      console.error('Failed to create affiliate:', error);
      toast.error(error.response?.data?.detail || 'Failed to create affiliate');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (affiliateId, newStatus) => {
    try {
      await affiliateAPI.updateStatus(affiliateId, newStatus);
      toast.success(`Affiliate ${newStatus === 'active' ? 'activated' : newStatus}`);
      fetchAffiliates();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const resetForm = () => {
    setFormData({
      contact_name: '',
      email: '',
      phone: '',
      company_name: '',
      commission_rate: 0.10,
      notes: ''
    });
  };

  const viewAffiliate = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setShowViewDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6" data-testid="affiliate-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-purple-600" />
            Affiliate Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your affiliate partners and track referral performance
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAffiliates} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-affiliate-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Affiliate
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Affiliates</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Affiliates</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Link2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
                <p className="text-2xl font-bold">{stats.totalReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-full">
                <DollarSign className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalEarnings)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, company, or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="affiliate-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Affiliates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Affiliates</CardTitle>
          <CardDescription>
            {affiliates.length} affiliate{affiliates.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : affiliates.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No affiliates found</p>
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Your First Affiliate
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Affiliate</TableHead>
                    <TableHead>Referral Code</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Referrals</TableHead>
                    <TableHead>Earnings</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.map((affiliate) => (
                    <TableRow key={affiliate.id} data-testid={`affiliate-row-${affiliate.id}`}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{affiliate.contact_name}</span>
                          <span className="text-sm text-muted-foreground">{affiliate.email}</span>
                          {affiliate.company_name && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {affiliate.company_name}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-1 bg-slate-100 rounded text-sm font-mono">
                            {affiliate.referral_code}
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => copyToClipboard(affiliate.referral_code, 'Referral code')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {(affiliate.commission_rate * 100).toFixed(0)}%
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{affiliate.total_referrals || 0} total</span>
                          <span className="text-xs text-green-600">
                            {affiliate.successful_referrals || 0} converted
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{formatCurrency(affiliate.total_earnings)}</span>
                          {(affiliate.pending_earnings || 0) > 0 && (
                            <span className="text-xs text-yellow-600">
                              {formatCurrency(affiliate.pending_earnings)} pending
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[affiliate.status] || statusColors.inactive}>
                          {affiliate.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(affiliate.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`affiliate-actions-${affiliate.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewAffiliate(affiliate)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => copyToClipboard(
                                `https://yoursite.com?ref=${affiliate.referral_code}`,
                                'Referral link'
                              )}
                            >
                              <Link2 className="h-4 w-4 mr-2" />
                              Copy Referral Link
                            </DropdownMenuItem>
                            {affiliate.status === 'active' ? (
                              <DropdownMenuItem 
                                onClick={() => handleStatusUpdate(affiliate.id, 'suspended')}
                                className="text-red-600"
                              >
                                <Pause className="h-4 w-4 mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => handleStatusUpdate(affiliate.id, 'active')}
                                className="text-green-600"
                              >
                                <Play className="h-4 w-4 mr-2" />
                                Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Create Affiliate Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Affiliate</DialogTitle>
            <DialogDescription>
              Create a new affiliate partner account. They will receive login credentials via email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="John Doe"
                  data-testid="affiliate-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="affiliate-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Acme Inc."
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="commission_rate">Commission Rate (%)</Label>
                <Input
                  id="commission_rate"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.commission_rate * 100}
                  onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) / 100 })}
                  placeholder="10"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Percentage earned on each successful referral
                </p>
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes about this affiliate..."
                  rows={3}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-affiliate-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Affiliate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Affiliate Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Affiliate Details</DialogTitle>
          </DialogHeader>
          
          {selectedAffiliate && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{selectedAffiliate.contact_name}</h3>
                    <Badge className={statusColors[selectedAffiliate.status]}>
                      {selectedAffiliate.status}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedAffiliate.email}</span>
                  </div>
                  {selectedAffiliate.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedAffiliate.phone}</span>
                    </div>
                  )}
                  {selectedAffiliate.company_name && (
                    <div className="flex items-center gap-2 text-sm col-span-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedAffiliate.company_name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Referral Code */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <Label className="text-xs text-muted-foreground">Referral Code</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-lg font-mono font-bold">
                    {selectedAffiliate.referral_code}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedAffiliate.referral_code, 'Referral code')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{selectedAffiliate.total_referrals || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Referrals</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{selectedAffiliate.successful_referrals || 0}</p>
                      <p className="text-xs text-muted-foreground">Converted</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatCurrency(selectedAffiliate.total_earnings)}
                      </p>
                      <p className="text-xs text-muted-foreground">Total Earnings</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-600">
                        {formatCurrency(selectedAffiliate.pending_earnings)}
                      </p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Commission */}
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm text-muted-foreground">Commission Rate</span>
                <span className="font-bold text-lg">
                  {(selectedAffiliate.commission_rate * 100).toFixed(0)}%
                </span>
              </div>

              {/* Notes */}
              {selectedAffiliate.notes && (
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <p className="mt-1 text-sm">{selectedAffiliate.notes}</p>
                </div>
              )}

              {/* Dates */}
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Created: {formatDate(selectedAffiliate.created_at)}</p>
                {selectedAffiliate.approved_at && (
                  <p>Approved: {formatDate(selectedAffiliate.approved_at)}</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temporary Password Dialog */}
      <Dialog open={!!tempPassword} onOpenChange={() => setTempPassword('')}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Affiliate Created Successfully</DialogTitle>
            <DialogDescription>
              Please share these credentials with the affiliate. The password should be changed on first login.
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Temporary Password</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-lg font-mono font-bold text-yellow-800">
                  {tempPassword}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(tempPassword, 'Password')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-yellow-700">
              This password will not be shown again. Make sure to copy it now.
            </p>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setTempPassword('')}>
              I've Copied the Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliateManagementPage;
